import React, { HTMLAttributes, useState } from 'react'
import { Collapse } from 'react-collapse'
import styled, { css } from 'styled-components'
import { useMediaQuery } from 'react-responsive'
import Box from '@components/Box'

import Arrow from '@icons/arrowDown.inline.svg'
import { STypographyDefault, STypographyDesc, STypographyParagraph } from '@components/Typography'
import { toPrice } from '@/helpers/toPrice'
import { above } from '@styles/mixins/breakpoints'
import { md } from '@styles/variables/breakpoints'
import { IFold } from '../types'

const SFold = styled(Box)`
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 8px;

  .ReactCollapse--collapse {
    transition: 0.3s;
  }
`

const SIcon = styled.div<{ isOpened: boolean }>`
  border-radius: 100%;
  width: 32px;
  height: 32px;
  padding-top: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.green};
  transition: 0.3s;
  flex-shrink: 0;
  flex-grow: 0;

  ${({ isOpened }) => isOpened && 'transform: rotate(180deg);'}
`

const SHead = styled(Box)`
  cursor: pointer;
`

const STag = styled(STypographyDesc)`
  display: inline;
  background: white;
  padding: 1px 10px;
  border-radius: 15px;

  ${above('md')(css`
    display: block;
    margin-left: 8px;
  `)}
`

const SCenterSection = styled(Box)`
  padding: 24px 0;
  border: 1px solid ${({ theme }) => theme.colors.middleGray};
  border-left: none;
  border-right: none;

  ${above('md')(css`
    display: flex;
    padding: 32px 0;
  `)}
`

const SDevider = styled.div`
  margin: 24px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.middleGray};

  ${above('xl')(css`
    width: 1px;
    min-height: 100%;
    border-top: 0;
    margin: 0 40px 0 19px;
    border-left: 1px solid ${({ theme }) => theme.colors.middleGray};
  `)}
`

const SGreenNumber = styled.div`
  color: ${({ theme }) => theme.colors.green};
  font-weight: 600;
  margin-bottom: 2px;
  line-height: 24px;

  font-size: 18px;
  ${above('md')(css`
    font-size: 20px;
  `)}
`

const Fold: React.FC<HTMLAttributes<HTMLDivElement> & IFold> = (props) => {
  const [isOpened, setIsOpened] = useState<boolean>(false)
  const isSm = useMediaQuery({ maxWidth: md - 1 })

  const notEnough = (
    <STypographyDefault color="darkGray">
      Накопленной суммы не достаточно для назначения пенсии в минимальном размере.
    </STypographyDefault>
  )

  const {
    className,
    heading,
    desc,
    value,
    valueDesc,
    taxed,
    numberOfSalaries,
    accruedInvestmentIncome,
    contributionsPaid,
    firstOptionTitle,
    firstOptionPension,
    firstOptionPayment,
    firstOptionPaymentDesc,
    firstOptionPensionDesc,
    secondOptionTitle,
    secondOptionPension,
    secondOptionPensionDesc,
  } = props

  const tag = <STag>{taxed ? 'Облагается НДФЛ' : 'НДФЛ не облагается'}</STag>

  return (
    <SFold className={className}>
      <SHead
        onClick={() => setIsOpened(!isOpened)}
        p={{ _: '20px', md: '32px', xl: '24px 40px' }}
        display="flex"
        justifyContent="space-between"
        alignItems={{ _: 'flex-start', xl: 'center' }}
      >
        <Box alignItems="center" display={{ _: 'block', xl: 'flex' }}>
          <Box width={{ _: '100%', xl: '340px' }} mr={{ _: '0', xl: '20px' }} mb={{ _: '24px', xl: '0' }}>
            <STypographyDefault fontWeight={600} mb="2px" fontSize={{ _: '18px', md: '20px' }} lineHeight="24px">
              {heading}
            </STypographyDefault>
            <STypographyDefault>{desc}</STypographyDefault>
          </Box>
          <Box>
            <Box display="flex" alignItems="center">
              <SGreenNumber>{toPrice(value)}</SGreenNumber>
              {!isSm && tag}
            </Box>
            <STypographyDefault mb={{ _: '20px', md: '0' }}>{valueDesc}</STypographyDefault>
          </Box>
          {isSm && tag}
        </Box>
        <SIcon isOpened={isOpened}>
          <Arrow />
        </SIcon>
      </SHead>
      <Collapse isOpened={isOpened} translate="yes">
        <Box pb={{ _: '40px', xl: '24px' }} px={{ _: '20px', md: '32px', xl: '40px' }}>
          <SCenterSection>
            <Box width={{ _: '167px', xl: '340px' }} mr={{ _: '32px', xl: '20px' }} flexShrink={0} flexGrow={0}>
              <STypographyParagraph fontWeight={600}>
                Из чего складывается
                <br />
                накопление
              </STypographyParagraph>
            </Box>
            <Box display={{ _: 'block', md: 'flex' }}>
              {numberOfSalaries ? (
                <Box display={{ _: 'block', md: 'flex', xl: 'block' }} mt={{ _: '20px', md: '0' }}>
                  <STypographyParagraph
                    mr="12px"
                    fontSize={{ _: '36px', xl: '16px' }}
                    lineHeight={{ _: '36px', xl: '16px' }}
                    fontWeight={{ _: 400, xl: 600 }}
                  >
                    {numberOfSalaries}
                  </STypographyParagraph>
                  <STypographyDefault>
                    Количество окладов в соответствии с Положением НПО Предприятия к пенсионному возрасту
                  </STypographyDefault>
                </Box>
              ) : ''}
              {contributionsPaid ? (
                <Box width="160px" mr="16px" mt={{ _: '20px', md: '0' }}>
                  <STypographyParagraph fontWeight={600}>{toPrice(contributionsPaid)}</STypographyParagraph>
                  <STypographyDefault>Уплачено взносов</STypographyDefault>
                </Box>
              ) : ''}
              {accruedInvestmentIncome ? (
                <Box mt={{ _: '12px', md: '0' }}>
                  <STypographyParagraph fontWeight={600}>{toPrice(accruedInvestmentIncome)}</STypographyParagraph>
                  <STypographyDefault>Начислено инвестиционного дохода</STypographyDefault>
                </Box>
              ) : ''}
            </Box>
          </SCenterSection>
          <STypographyParagraph my="32px" fontWeight={600}>
            Варианты выплаты
          </STypographyParagraph>
          <Box display={{ _: 'block', xl: 'flex' }}>
            <Box width={{ _: '100%', md: '640px' }} display={{ _: 'block', md: 'flex' }}>
              <SGreenNumber>01</SGreenNumber>
              <Box pt="4px" ml={{ _: '0', md: '8px' }} width="100%">
                <Box width={{ _: '100%', xl: '405px' }} mb="24px">
                  <STypographyDefault fontWeight={600}>{firstOptionTitle}</STypographyDefault>
                </Box>
                <Box display={{ _: 'block', md: 'flex' }}>
                  <Box width={{ _: '100%', md: '45%' }} mr={{ _: '0', md: '5%' }}>
                    {firstOptionPayment > 0 ? (
                      <>
                        <STypographyParagraph mb="6px" fontWeight={600}>
                          {toPrice(firstOptionPayment)}
                        </STypographyParagraph>
                        <STypographyDefault color="darkGray">{firstOptionPaymentDesc}</STypographyDefault>
                      </>
                    ) : (
                      notEnough
                    )}
                  </Box>
                  <Box width={{ _: '100%', md: '45%' }} mt={{ _: '12px', md: '0' }} ml={{ _: '0', md: '5%' }}>
                    {firstOptionPension > 0 ? (
                      <>
                        <STypographyParagraph mb="6px" fontWeight={600}>
                          {toPrice(firstOptionPension, ' ₽/мес')}
                        </STypographyParagraph>
                        <STypographyDefault color="darkGray">{firstOptionPensionDesc}</STypographyDefault>
                      </>
                    ) : (
                      notEnough
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
            <SDevider />
            <Box width={{ _: '100%', xl: '400px' }} display={{ _: 'block', md: 'flex' }}>
              <SGreenNumber>02</SGreenNumber>
              <Box pt="4px" ml={{ _: '0', md: '8px' }} width="100%">
                <Box width={{ _: '100%', md: '405px' }} mb="24px">
                  <STypographyDefault fontWeight={600}>{secondOptionTitle}</STypographyDefault>
                </Box>
                {secondOptionPension > 0 ? (
                  <>
                    <STypographyParagraph mb="6px" fontWeight={600}>
                      {toPrice(secondOptionPension, ' ₽/мес')}
                    </STypographyParagraph>
                    <STypographyDefault color="darkGray">{secondOptionPensionDesc}</STypographyDefault>
                  </>
                ) : (
                  notEnough
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </SFold>
  )
}

export default Fold
